import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp, ExternalLink, Shield, Award } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

// Distinct visual treatment per design tag
const COVERS = {
  cafe: "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?auto=format&fit=crop&w=1200&q=80",
  industrial: "https://images.unsplash.com/photo-1535813547-99c456a41d4a?auto=format&fit=crop&w=1200&q=80",
  tech: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
  fashion: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80",
};

const TINTS = {
  cafe: "from-amber-500/35 to-orange-500/10",
  industrial: "from-orange-600/40 to-zinc-500/15",
  tech: "from-cyan-500/35 to-blue-500/10",
  fashion: "from-fuchsia-500/35 to-violet-500/10",
};

export default function Portfolio() {
  const { t } = useLang();
  const items = t.portfolio.items;

  return (
    <section
      id="portfolio"
      className="py-20 md:py-28 bg-[#050814] text-white relative overflow-hidden"
      data-testid="portfolio-section"
    >
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="absolute top-1/3 -left-40 w-[28rem] h-[28rem] rounded-full bg-blue-600/20 blur-3xl" />
      <div className="absolute bottom-0 -right-32 w-[28rem] h-[28rem] rounded-full bg-violet-600/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
            {t.portfolio.eyebrow}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-3">{t.portfolio.title}</h2>
          <p className="text-slate-300 mt-4 text-base md:text-lg">{t.portfolio.subtitle}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {items.map((p, i) => {
            const isIndustrial = p.design === "industrial";
            const Wrapper = p.url ? "a" : "div";
            const wrapperProps = p.url
              ? { href: p.url, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                data-testid={`portfolio-${i}`}
              >
                <Wrapper
                  {...wrapperProps}
                  className={`group relative block rounded-3xl overflow-hidden border bg-slate-900/40 hover:border-white/30 transition ${
                    isIndustrial ? "border-orange-500/30" : "border-white/10"
                  }`}
                >
                  {/* Cover image */}
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={COVERS[p.design] || COVERS.cafe}
                      alt={p.t}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                      loading="lazy"
                    />
                    <div
                      className={`absolute inset-0 bg-gradient-to-tr ${TINTS[p.design] || TINTS.cafe} mix-blend-overlay`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

                    {/* Industrial overlay — rivets + steel pattern */}
                    {isIndustrial && (
                      <>
                        <div
                          className="absolute inset-0 opacity-30 mix-blend-overlay"
                          style={{
                            backgroundImage:
                              "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 14px)",
                          }}
                        />
                        <div className="absolute top-3 left-3 flex gap-1.5">
                          {[0, 1, 2].map((d) => (
                            <span
                              key={d}
                              className="w-2 h-2 rounded-full bg-zinc-300/60 ring-2 ring-zinc-900/50"
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Pride badge for industrial */}
                    {isIndustrial && (
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-slate-950 text-[11px] font-bold uppercase tracking-wider shadow-lg">
                        <Award className="w-3.5 h-3.5" /> Proud Build
                      </div>
                    )}

                    {/* Live link badge */}
                    {p.url && (
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-lg">
                        <span className="relative flex w-2 h-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                        </span>
                        Live
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 inset-x-0 p-6 md:p-7 flex items-end justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                          isIndustrial ? "text-amber-300" : "text-blue-300"
                        }`}
                      >
                        {p.c}
                      </div>
                      <h3 className="font-display text-xl md:text-2xl font-bold mt-1">{p.t}</h3>
                      <div
                        className={`mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold ${
                          isIndustrial ? "text-amber-300" : "text-emerald-300"
                        }`}
                      >
                        <TrendingUp className="w-4 h-4" /> {p.k}
                      </div>
                      {p.proud && (
                        <p className="text-[12px] text-slate-300 mt-2 max-w-md leading-relaxed">
                          <Shield className="inline w-3 h-3 mr-1 -mt-0.5 text-amber-300" />
                          {p.proud}
                        </p>
                      )}
                    </div>
                    <div className="w-11 h-11 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-slate-950 transition flex-shrink-0">
                      {p.url ? <ExternalLink className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                    </div>
                  </div>
                </Wrapper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
